/* eslint-disable max-lines */
import { useZoneOccupancy } from '@hakimo-ui/hakimo/data-access';
import {
  PresenceLatest,
  PresenceLatestItem,
  PresenceRule,
} from '@hakimo-ui/hakimo/types';
import { OccupancyChart } from '@hakimo-ui/hakimo/ui-elements';
import { Alert } from '@hakimo-ui/shared/ui-base';
import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect, useMemo, useState } from 'react';
import { ruleTargetsZone, zoneLabel } from './ruleSentence';

dayjs.extend(utc);

interface Props {
  latest: PresenceLatest | undefined;
  isLoading: boolean;
  rules: PresenceRule[];
}

/**
 * How full each zone is now, and what the day looked like.
 *
 * The headline is the last observed count, not a live one: the camera only
 * uploads when something moves, so a number is only as current as the last
 * clip. When the camera has been quiet longer than its habit the count
 * greys out and says so, rather than presenting a stale figure as now.
 */
export function OccupancyCard({ latest, isLoading, rules }: Props) {
  const items = useMemo(() => latest?.items ?? [], [latest]);
  const [zoneId, setZoneId] = useState<string | null>(null);

  // Default to the first named zone, falling back to the whole camera.
  useEffect(() => {
    if (zoneId && items.some((i) => i.zone.id === zoneId)) return;
    const first = items.find((i) => !i.zone.isDefault) ?? items[0];
    setZoneId(first?.zone.id ?? null);
  }, [items, zoneId]);

  const selected = items.find((i) => i.zone.id === zoneId);
  const since = useMemo(
    () => dayjs().subtract(24, 'hour').startOf('hour').toISOString(),
    // Re-evaluated when the latest payload changes, roughly every 30s.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [latest?.nowUtc]
  );
  const occupancy = useZoneOccupancy(
    zoneId ?? '',
    { from: since, bucket: '15m' },
    !!zoneId
  );
  const limit = useMemo(() => {
    if (!selected) return null;
    const forZone = rules.filter(
      (r) =>
        r.enabled &&
        r.type === 'max_occupancy' &&
        ruleTargetsZone(r, selected.zone)
    );
    return forZone.length ? Math.min(...forZone.map((r) => r.count)) : null;
  }, [rules, selected]);

  if (isLoading) {
    return (
      <p className="text-onlight-text-3 dark:text-ondark-text-3 p-4 text-sm">
        Loading occupancy…
      </p>
    );
  }
  if (!latest || items.length === 0) {
    return (
      <Alert type="info">
        No occupancy yet. Counts appear once the camera has recorded presence in
        a zone.
      </Alert>
    );
  }

  return (
    <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 space-y-4 rounded-lg p-4">
      <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <ZoneChip
            key={item.zone.id}
            item={item}
            selected={item.zone.id === zoneId}
            onClick={() => setZoneId(item.zone.id)}
          />
        ))}
      </div>

      <LivenessLine liveness={latest.liveness} />

      {selected && (
        <div>
          {/* No footfall headline: distinct-people counts are not reliable
              until identity resolution improves, so the card shows only
              how many were in view. */}
          <h4 className="text-onlight-text-1 dark:text-ondark-text-1 mb-2 text-sm font-semibold">
            {zoneLabel(selected.zone.name)} · last 24 hours
          </h4>
          {occupancy.data ? (
            <OccupancyChart
              occupancy={occupancy.data.items}
              threshold={limit}
            />
          ) : (
            <p className="text-onlight-text-3 dark:text-ondark-text-3 text-sm">
              {occupancy.isError
                ? occupancy.error?.message
                : 'Loading the day…'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

interface ChipProps {
  item: PresenceLatestItem;
  selected: boolean;
  onClick: () => void;
}

function ZoneChip({ item, selected, onClick }: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'rounded-full border px-3 py-1 text-sm',
        selected
          ? 'border-teal-600 bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300'
          : 'border-onlight-line-2 dark:border-ondark-line-2 text-onlight-text-2 dark:text-ondark-text-2'
      )}
    >
      {zoneLabel(item.zone.name)}
    </button>
  );
}

function LivenessLine({ liveness }: { liveness: PresenceLatest['liveness'] }) {
  // A naive UTC string from the API, shown in the viewer's own timezone.
  const last = liveness.lastClipUtc
    ? dayjs.utc(liveness.lastClipUtc).local().format('HH:mm')
    : null;
  return (
    <p className="text-onlight-text-2 dark:text-ondark-text-2 flex items-center gap-2 text-xs">
      <span
        className={clsx(
          'inline-block h-2 w-2 rounded-full',
          liveness.live ? 'bg-green-500' : 'bg-amber-500'
        )}
      />
      {liveness.live
        ? `Camera live · last clip ${last}`
        : last
        ? `No clips since ${last} — counts may be stale`
        : 'No clips in the last six hours — counts may be stale'}
    </p>
  );
}

export default OccupancyCard;
